'use client'

import * as React from 'react'
import { ImgWithFallback } from '@/components/ui/img-with-fallback'
import { ArrowUpRight, AtSign, Check, ImagePlus, MailCheck, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { appSignupUrl } from '@/lib/widget/embed'

// Sample "mentions stream". Ticks through sample posts to show the journey
// a tagged post takes: caught as a mention, sent a rights request, then
// added to a shoppable gallery. The three states are visualised as a
// pipeline. Everything is SAMPLE content (verified Unsplash IDs reused from
// the repo) with obviously-fictional handles, so nothing reads as a real
// brand mention or quote.
//
// Motion: the ticker auto-advances ONLY when the user has not asked for
// reduced motion. Under prefers-reduced-motion: reduce there is no
// auto-advance and no animation; the visitor steps through with the
// dot controls instead.

type Stage = 'mention' | 'requested' | 'added'

type Mention = {
  id: string
  handle: string
  text: string
  imageId: string
  stage: Stage
}

const MENTIONS: Mention[] = [
  { id: 'm1', handle: '@sample.creator', text: 'tagged you in a try-on haul', imageId: 'photo-1490481651871-ab68de25d43d', stage: 'mention' },
  { id: 'm2', handle: '@sample.daily', text: 'mentioned your brand in a reel', imageId: 'photo-1503454537195-1dcabb73ffb9', stage: 'requested' },
  { id: 'm3', handle: '@sample.field', text: 'posted a one-month review', imageId: 'photo-1556228720-195a672e8a03', stage: 'added' },
  { id: 'm4', handle: '@sample.styles', text: 'shared a fit check with the new drop', imageId: 'photo-1483985988355-763728e1935b', stage: 'mention' },
  { id: 'm5', handle: '@sample.notes', text: 'left a glowing thread about the kit', imageId: 'photo-1469334031218-e382a71b716b', stage: 'requested' },
]

const STAGES: { value: Stage; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: 'mention', label: 'Mention caught', icon: AtSign },
  { value: 'requested', label: 'Rights requested', icon: MailCheck },
  { value: 'added', label: 'Added to gallery', icon: ImagePlus },
]

const STAGE_INDEX: Record<Stage, number> = { mention: 0, requested: 1, added: 2 }

const STAGE_COPY: Record<Stage, string> = {
  mention: 'Caught in your mentions stream',
  requested: 'Rights request sent, awaiting consent',
  added: 'Live in a shoppable gallery',
}

const unsplash = (id: string, w = 240, h = 240) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&h=${h}&q=80`

function usePrefersReducedMotion() {
  const [reduced, setReduced] = React.useState(false)
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduced(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])
  return reduced
}

export function SocialListeningPreview() {
  const reduced = usePrefersReducedMotion()
  const [index, setIndex] = React.useState(0)
  const [paused, setPaused] = React.useState(false)

  // Auto-advance only when motion is allowed and the user is not hovering /
  // focused inside the panel.
  React.useEffect(() => {
    if (reduced || paused) return
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % MENTIONS.length)
    }, 3200)
    return () => window.clearInterval(id)
  }, [reduced, paused])

  const current = MENTIONS[index]
  const currentStageIndex = STAGE_INDEX[current.stage]

  return (
    <div
      className="overflow-hidden rounded-2xl border bg-card shadow-sm"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <div className="grid gap-px bg-border md:grid-cols-[1.1fr_1fr]">
        {/* Left: the live mention card */}
        <div className="bg-card p-5 sm:p-6">
          <div className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold text-foreground">
              <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden />
              Mentions stream
            </span>
            <span className="rounded-full border px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
              Sample data
            </span>
          </div>

          {/* aria-live so screen readers hear each new mention as it ticks */}
          <div className="mt-5" aria-live="polite" aria-atomic="true">
            <div className="flex items-start gap-4">
              <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-secondary">
                <ImgWithFallback
                  src={unsplash(current.imageId)}
                  alt={`Sample tagged post from ${current.handle}`}
                  fill
                  sizes="64px"
                  className="object-cover"
                />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">{current.handle}</p>
                <p className="mt-0.5 text-sm text-muted-foreground">{current.text}</p>
                <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary">
                  <Check className="h-3.5 w-3.5" aria-hidden />
                  {STAGE_COPY[current.stage]}
                </p>
              </div>
            </div>
          </div>

          {/* Step controls (also the reduced-motion stepper) */}
          <div className="mt-6 flex items-center gap-2" role="group" aria-label="Step through sample mentions">
            {MENTIONS.map((m, i) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Show sample mention ${i + 1} of ${MENTIONS.length}`}
                aria-current={i === index ? 'true' : undefined}
                className={cn(
                  'h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  i === index ? 'w-7 bg-primary' : 'w-2.5 bg-border hover:bg-muted-foreground/40',
                )}
              />
            ))}
            <span className="ml-auto text-[11px] text-muted-foreground">
              {reduced ? 'Tap a dot to step through' : 'Auto-advancing'}
            </span>
          </div>
        </div>

        {/* Right: the three-state pipeline */}
        <div className="bg-secondary/40 p-5 sm:p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            What happens next
          </p>
          <ol className="mt-4 space-y-3">
            {STAGES.map((s, i) => {
              const reached = i <= currentStageIndex
              const Icon = s.icon
              return (
                <li
                  key={s.value}
                  className={cn(
                    'flex items-center gap-3 rounded-xl border p-3 transition-colors duration-200 ease-out',
                    reached ? 'border-primary/40 bg-card' : 'border-dashed bg-card/50',
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                      reached ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground',
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className={cn('text-sm font-semibold', reached ? 'text-foreground' : 'text-muted-foreground')}>
                      {s.label}
                    </p>
                    <p className="text-xs text-muted-foreground">Step {i + 1} of 3</p>
                  </div>
                  {reached && (
                    <Check className="ml-auto h-4 w-4 shrink-0 text-primary" aria-hidden />
                  )}
                </li>
              )
            })}
          </ol>
        </div>
      </div>

      {/* Honest footer + CTAs */}
      <div className="flex flex-col gap-3 border-t bg-card p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <Check className="h-3.5 w-3.5 text-primary" aria-hidden />
          Sample mentions for illustration. Catch your real ones once you connect the source in-app.
        </p>
        <div className="flex flex-wrap gap-2">
          <a
            href="/tools/widget-builder?source=hashtag"
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3.5 py-2 text-xs font-semibold text-primary-foreground transition-colors duration-150 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
          >
            Build from mentions
            <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
          </a>
          <a
            href={appSignupUrl({ source: 'hashtag', ref: 'tool-social-listening' })}
            className="inline-flex items-center rounded-lg border px-3.5 py-2 text-xs font-medium text-muted-foreground transition-colors duration-150 hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Start listening in app
          </a>
        </div>
      </div>
    </div>
  )
}
